import { useMutation, useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { createContext, useContext, useEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { NovaScheduleCreateView, type ManualNovaScheduleCreateInput } from './nova-schedule-create-view';
import { NovaSchedulesList } from './nova-schedules-list';
import { NOVA_SCHEDULES_FEATURE_FLAG } from '../../../../lib/nova-schedule-proposal';
import { GET_NOVA_SCHEDULES, isNovaScheduleRecord } from '../../../../queries/nova-schedule-management';
import { CREATE_NOVA_SCHEDULE, type CreateNovaScheduleCommand } from '../../../../queries/nova-schedule-mutations';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { useNovaNavigationSafe } from '../../navigation/nova-navigation-context';
import { useSettingsPanelContext } from '../settings-panel-context';

const MANAGEABLE_SCHEDULE_STATUSES = ['ACTIVE', 'PAUSED', 'COMPLETED'] as const;

function useSchedulesSettings() {
  const schedulesEnabled = useFeatureFlag(NOVA_SCHEDULES_FEATURE_FLAG, false);
  const { userScopes, userUuid, startNewChatWithInput } = useChatOverlayContext();
  const { pushView } = useSettingsPanelContext();
  const navigation = useNovaNavigationSafe();
  const [localIsCreating, setLocalIsCreating] = useState(false);
  const isCreating = navigation ? navigation.view.kind === 'scheduled' && navigation.view.isCreating : localIsCreating;
  const setIsCreating = (creating: boolean) => {
    if (navigation) {
      if (creating) navigation.actions.createSchedule();
      else navigation.actions.closeSubView();
    } else setLocalIsCreating(creating);
  };
  const [createSchedule, createState] = useMutation(CREATE_NOVA_SCHEDULE);
  const { data, loading, error, refetch } = useQuery(GET_NOVA_SCHEDULES, {
    variables: {
      filters: {
        ownerUserUuids: [userUuid],
        statuses: [...MANAGEABLE_SCHEDULE_STATUSES],
      },
    },
    skip: !schedulesEnabled || !userUuid,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const offeredEmptyCreateRef = useRef(false);
  const emptyListReady = !loading && !error && data?.getNovaSchedules.length === 0;
  useEffect(() => {
    if (offeredEmptyCreateRef.current || !emptyListReady || !navigation || navigation.view.kind !== 'scheduled') return;
    offeredEmptyCreateRef.current = true;
    if (!navigation.view.scheduleUuid && !navigation.view.isCreating) {
      // Keep the existing first-visit empty-state form, while allowing Back/Escape to reach its parent list.
      navigation.actions.canonicalize({ kind: 'scheduled', isCreating: true });
    }
  }, [emptyListReady, navigation]);

  const schedules = data?.getNovaSchedules.filter(isNovaScheduleRecord) ?? [];
  const showCreateView = isCreating || (!navigation && !loading && !error && schedules.length === 0);

  const createManualSchedule = async (input: ManualNovaScheduleCreateInput) => {
    const scheduleUuid = uuidv4();
    const command: CreateNovaScheduleCommand = {
      uuid: scheduleUuid,
      ownerUserUuid: userUuid,
      ...(input.account?.uuid ? { accountUuid: input.account.uuid } : {}),
      title: input.title,
      prompt: input.prompt,
      ...(input.attachmentUploads.length ? { attachmentUploads: input.attachmentUploads } : {}),
      timezone: input.timezone,
      recurrence: input.recurrence,
      ...(input.startAt ? { startAt: input.startAt } : {}),
      ...(input.outputPolicy ? { outputPolicy: input.outputPolicy } : {}),
    };

    try {
      const result = await createSchedule({ variables: { command } });
      if (result.data?.createNovaSchedule.uuid !== scheduleUuid) {
        throw new Error('The create mutation did not return the requested schedule.');
      }
      // Leave the form first: during a refetch the Save button is live again, and a second click
      // would create a duplicate.
      if (navigation) navigation.actions.scheduleCreated(scheduleUuid);
      else {
        setIsCreating(false);
        pushView({ type: 'nova-schedule-detail', scheduleUuid });
      }
      void refetch();
    } catch {
      // Apollo exposes the error through createState while the form stays open.
    }
  };

  const createProps: ComponentProps<typeof NovaScheduleCreateView> = {
    showBack: schedules.length > 0,
    loggedInUserUuid: userUuid,
    userScopes: userScopes ?? [],
    isSaving: createState.loading,
    error: createState.error,
    onBack: () => setIsCreating(false),
    onPromptSubmit: (prompt, { chatUuid, attachmentUploads }) => {
      startNewChatWithInput(prompt, {
        chatUuid,
        additionalContext: {
          attachmentUploads,
          messageMetadata: { files: attachmentUploads.map(({ path, name }) => ({ path, filename: name })) },
        },
        submitImmediately: true,
      });
    },
    onManualSubmit: createManualSchedule,
  };
  const listProps: ComponentProps<typeof NovaSchedulesList> = {
    schedules,
    isLoading: loading && data === undefined,
    error,
    onNewSchedule: () => setIsCreating(true),
    onRetry: () => void refetch(),
    onSelectSchedule: (scheduleUuid) => pushView({ type: 'nova-schedule-detail', scheduleUuid }),
  };
  return { schedulesEnabled, showCreateView, createProps, listProps };
}

const SchedulesContext = createContext<ReturnType<typeof useSchedulesSettings> | null>(null);

/** Shared controller stays mounted across the list/create/detail routes. */
export function NovaSchedulesProvider({ children }: { children: ReactNode }) {
  const value = useSchedulesSettings();
  return <SchedulesContext.Provider value={value}>{children}</SchedulesContext.Provider>;
}

function useSchedules() {
  const context = useContext(SchedulesContext);
  if (!context) throw new Error('Schedule screens require NovaSchedulesProvider');
  return context;
}

export function NovaSchedulesListScreen() {
  const { schedulesEnabled, listProps } = useSchedules();
  return schedulesEnabled ? <NovaSchedulesList {...listProps} /> : null;
}

export function NovaScheduleCreateScreen() {
  const { schedulesEnabled, createProps } = useSchedules();
  return schedulesEnabled ? <NovaScheduleCreateView {...createProps} /> : null;
}

function MemoryScheduleScreen() {
  const { showCreateView } = useSchedules();
  return showCreateView ? <NovaScheduleCreateScreen /> : <NovaSchedulesListScreen />;
}

/** Embedded/manual surfaces retain their existing in-memory list/create selection. */
export function NovaSchedulesSettings() {
  return (
    <NovaSchedulesProvider>
      <MemoryScheduleScreen />
    </NovaSchedulesProvider>
  );
}
